import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EPCBadge } from "../components/EPCBadge";
import { Hammer, Calendar, PoundSterling, ClipboardCheck, Activity, AlertCircle, Search, ArrowUpRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATE_COLORS = {
  planned:     { bg: "#f1f5f9", border: "#cbd5e1", text: "#334155", solid: "#475569" },
  instructed:  { bg: "#fef9c3", border: "#facc15", text: "#854d0e", solid: "#ca8a04" },
  in_progress: { bg: "#fef3c7", border: "var(--hi-orange)", text: "var(--hi-orange-deep)", solid: "var(--hi-orange)" },
  completed:   { bg: "#dcfce7", border: "#22c55e", text: "#166534", solid: "#16a34a" },
  on_hold:     { bg: "#fee2e2", border: "#f87171", text: "#991b1b", solid: "#ef4444" },
  cancelled:   { bg: "#f1f5f9", border: "#94a3b8", text: "#475569", solid: "#64748b" },
};

const GBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

export default function DeliveryPage() {
  const [cockpit, setCockpit] = useState(null);
  const [states, setStates] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "all", search: "", contractor: "" });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (filters.state && filters.state !== "all") params.state = filters.state;
      if (filters.search) params.search = filters.search;
      if (filters.contractor) params.contractor = filters.contractor;
      const [c, s, p] = await Promise.all([
        api.get("/delivery/cockpit"),
        api.get("/delivery/states"),
        api.get("/delivery/programme", { params }),
      ]);
      setCockpit(c.data);
      setStates(s.data.states || []);
      setRows(p.data.items || []);
      setTotal(p.data.total || 0);
    } catch (e) {
      toast.error("Failed to load delivery cockpit", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filters.state, filters.search, filters.contractor, page]);

  useEffect(() => { load(); }, [load]);

  const stateBuckets = useMemo(() => {
    if (!cockpit?.state_counts) return [];
    return cockpit.state_counts.map((b) => ({ ...b, color: STATE_COLORS[b.state] }));
  }, [cockpit]);

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="delivery-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--hi-orange)" }}>PROGRAMME COCKPIT</div>
          <h1 className="font-heading font-black text-4xl mt-2 tracking-tight" style={{ color: "var(--hi-olive)" }}>
            Project delivery<br /><span style={{ color: "var(--hi-orange)" }}>tracking.</span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-3xl">
            From <em>planned</em> through <em>instructed</em>, <em>in&nbsp;progress</em>, to <em>completed</em> — every retrofit measure across the portfolio, in one cockpit.
          </p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="delivery-kpis">
        {loading || !cockpit ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)
        ) : (
          <>
            <KPI icon={<Activity className="h-5 w-5" />} label="Active" value={cockpit.active_count} accent="var(--hi-orange)" data-testid="kpi-active" />
            <KPI icon={<ClipboardCheck className="h-5 w-5" />} label="Completed" value={cockpit.completed_count} accent="#16a34a" data-testid="kpi-completed" />
            <KPI icon={<Hammer className="h-5 w-5" />} label="Committed" value={GBP(cockpit.committed_cost)} accent="var(--hi-olive)" data-testid="kpi-committed" sub="Instructed + WIP" />
            <KPI icon={<PoundSterling className="h-5 w-5" />} label="Spent" value={GBP(cockpit.spent_cost)} accent="#0f172a" data-testid="kpi-spent" sub="Completed only" />
          </>
        )}
      </div>

      {/* Kanban — state lanes */}
      <Card className="rounded-none border-border p-6" data-testid="state-lanes">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
          <Hammer className="h-3.5 w-3.5" /> State breakdown — click a lane to filter the table below
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {stateBuckets.map((b) => (
            <button
              key={b.state}
              onClick={() => { setFilters((f) => ({ ...f, state: f.state === b.state ? "all" : b.state })); setPage(1); }}
              className={`text-left p-3 border-2 transition-transform hover:translate-y-[-2px] ${filters.state === b.state ? "ring-2 ring-offset-2" : ""}`}
              style={{ backgroundColor: b.color?.bg, borderColor: b.color?.border, color: b.color?.text }}
              data-testid={`lane-${b.state}`}
            >
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{b.label}</div>
              <div className="font-heading font-black text-3xl mt-1">{b.count}</div>
            </button>
          ))}
        </div>
        {cockpit?.top_contractors?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Top contractors</div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="top-contractors">
              {cockpit.top_contractors.map((c) => (
                <button
                  key={c.name}
                  onClick={() => { setFilters((f) => ({ ...f, contractor: f.contractor === c.name ? "" : c.name })); setPage(1); }}
                  className={`px-2 py-1 text-xs border ${filters.contractor === c.name ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                >
                  {c.name} <span className="opacity-60">· {c.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Filters + table */}
      <Card className="rounded-none border-border p-0" data-testid="programme-list">
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.search} onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(1); }} placeholder="Search by address or postcode…" className="rounded-none border-slate-300 pl-10 h-10" data-testid="delivery-search" />
          </div>
          <Select value={filters.state} onValueChange={(v) => { setFilters((f) => ({ ...f, state: v })); setPage(1); }}>
            <SelectTrigger className="rounded-none border-slate-300 h-10 w-44" data-testid="delivery-state-filter"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {states.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={filters.contractor} onChange={(e) => { setFilters((f) => ({ ...f, contractor: e.target.value })); setPage(1); }} placeholder="Contractor…" className="rounded-none border-slate-300 h-10 w-44" data-testid="delivery-contractor-filter" />
          <div className="text-xs text-muted-foreground font-mono">{total} row(s)</div>
        </div>
        {loading ? (
          <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="delivery-empty">
            <AlertCircle className="h-6 w-6 mx-auto mb-2 text-slate-300" />
            No delivery records match your filters yet.<br />
            <span className="text-xs">Tip — open any property and set a measure to <em>Planned</em> or <em>Instructed</em> to start tracking.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="delivery-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2">Property</th>
                  <th className="text-left px-3 py-2">Measure</th>
                  <th className="text-left px-3 py-2">State</th>
                  <th className="text-left px-3 py-2">Contractor</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Actual £</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = STATE_COLORS[r.state] || STATE_COLORS.planned;
                  const date = r.completed_date || r.started_date || r.instructed_date || (r.updated_at ? r.updated_at.slice(0, 10) : "—");
                  return (
                    <tr key={`${r.property_id}-${r.measure_id}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`delivery-row-${r.property_id}-${r.measure_id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <EPCBadge band={r.current_band} size="sm" />
                          <div className="min-w-0">
                            <a href={`/properties?selected=${r.property_id}`} className="font-medium hover:underline truncate inline-flex items-center gap-1">
                              {r.address} <ArrowUpRight className="h-3 w-3 opacity-40" />
                            </a>
                            <div className="text-[10px] text-muted-foreground font-mono">{r.postcode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">{r.label}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border" style={{ backgroundColor: c.bg, borderColor: c.border, color: c.text }}>
                          {r.state.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{r.contractor || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs font-mono"><Calendar className="h-3 w-3 inline mr-1 text-muted-foreground" />{date}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.actual_cost ? GBP(r.actual_cost) : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 50 && (
          <div className="p-3 border-t border-slate-100 flex items-center justify-between text-xs">
            <div>Page {page} of {Math.ceil(total / 50)}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-none border-slate-300" data-testid="delivery-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-none border-slate-300" data-testid="delivery-next">Next</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="rounded-none border-dashed border-slate-300 p-4 text-xs text-slate-600 leading-relaxed flex items-start gap-2">
        <Building2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--hi-orange)" }} />
        <span>
          <strong>How it works.</strong> Every measure on every property — gov-recommended, manually added, or imported from a job card — carries a delivery state. Open a property, scroll to the Retrofit Cost Estimator, and click the state badge on any row to update <em>contractor, dates, actual cost, notes</em>. The cockpit aggregates the lot.
        </span>
      </Card>
    </div>
  );
}

function KPI({ icon, label, value, sub, accent, ...rest }) {
  return (
    <Card className="rounded-none border-border p-5" {...rest}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>{icon}{label}</div>
      <div className="font-heading font-black text-3xl mt-2" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}
